import { batch } from 'react-redux';
import { TransactionNotFound } from 'src/common/helpers/error-code';
import { store } from '../../common/redux/configure-store';
import { popupMessage } from '../../messages/business-logic/popup-message';
import { getExemptibleTax, getExemptibleTaxByLine } from './service/get-exemptible-tax';
import { transactionEnd } from '../../home-page/business-logic/transaction-end';
import { ExemptibleTax } from '../interfaces/exemptible-tax.interface';
import { exemptTax, exemptTaxByLine } from './service/apply-tax-exemption';
import { ApplyTaxExemptionDTO } from '../interfaces/tax-exemption.interface';
import { LOCAL_ISO_CURRENCY_CODE } from 'src/common/helpers/localized-description-helper';
import { ItemListActions } from '../../common/redux/reducers/item-list-reducer';
import { CartResponse } from 'src/items-list/business-logic/service/interfaces/cart-response';
import { convertCartResponse } from 'src/common/converters/cart-response-journal-converter';
import { getItemExemptedTax } from '../../common/helpers/tax-exemptions-helper';
import { EJRecordTypes } from 'src/common/enumerations/ej-record-types';
import { TransactionActions } from 'src/common/redux/reducers/transaction-reducer';
import { isEmeraldSellingService } from 'src/xlr-configuration/pos-service-type-utils';
import { showMultipleAuthoritiesModal } from '../components/multiple-authorities-modal';
import { ListItemData } from 'src/common/components/multi-select-list/multi-select-list';
import { handleTaxException, TaxExemptionData } from '../helper/tax-exemption-helper';

let _allAuths: ExemptibleTax[];

export const exemptTransactionTax = async (): Promise<void> => {
  const transactionId = store.getState().transaction.transactionId;
  if (transactionId) {
    await getExemptibleTax(transactionId)
      .then(async (taxList: ExemptibleTax[]) => {
        _allAuths = taxList;
        const { currentlyNotExempted, currentlyExempted } = separateAuthorities(taxList);
        const cartResponse = await modifyExemptions(
          transactionId,
          currentlyNotExempted,
          currentlyExempted,
        );
        updateEJState(cartResponse);
      })
      .catch((err): void => {
        if (err.code === TransactionNotFound) {
          transactionEnd();
        } else {
          popupMessage(err);
        }
      });
  }
};

export const exemptLineTax = async (): Promise<void> => {
  const transactionId = store.getState().transaction.transactionId;
  const lineId = store.getState().itemList.selectedItemSequenceNumber.toString();
  if (transactionId && !!lineId) {
    await getExemptibleTaxByLine(transactionId, lineId)
      .then(async (taxList: ExemptibleTax[]) => {
        _allAuths = taxList;
        const { currentlyNotExempted, currentlyExempted } = separateAuthorities(taxList);
        const ejItem = store
          .getState()
          .itemList.ejItems.find(item => item.SequenceNumber.toString() === lineId);
        const cartResponse = await modifyExemptions(
          transactionId,
          ejItem && getItemExemptedTax(ejItem) === 0 ? currentlyNotExempted : [],
          currentlyExempted,
          lineId,
        );
        updateEJState(cartResponse);
      })
      .catch((err): void => {
        if (err.code === TransactionNotFound) {
          transactionEnd();
        } else {
          popupMessage(err);
        }
      });
  }
};

export const updateEJState = (cartResponse: CartResponse): void => {
  if (Object.keys(cartResponse).length === 0) {
    return;
  }

  const convertedResponse = convertCartResponse(cartResponse);
  const currency = cartResponse.cartInfo?.defaultCurrency ?? LOCAL_ISO_CURRENCY_CODE;
  const { selectedItemSequenceNumber } = store.getState().itemList;
  const selectedItem = store
    .getState()
    .itemList.ejItems.find(
      ({ SequenceNumber, Type }) =>
        SequenceNumber === selectedItemSequenceNumber && Type !== EJRecordTypes.TaxExemption,
    );

  batch((): void => {
    if (isEmeraldSellingService()) {
      store.dispatch(ItemListActions.removeAllRecords());
    }
    store.dispatch(ItemListActions.updateItems(cartResponse.items ?? []));
    store.dispatch(ItemListActions.updateEJ(convertedResponse));
    store.dispatch(ItemListActions.setTotals(cartResponse.cartInfo?.totals ?? {}, currency));
    if (selectedItem) {
      const { Type, PromotionId } = selectedItem;
      store.dispatch(ItemListActions.selectItem(selectedItemSequenceNumber, Type, PromotionId));
    }
  });
};

export const modifyExemptions = async (
  transactionId: string,
  currentlyNotExempted: ExemptibleTax[],
  currentlyExempted: ExemptibleTax[],
  lineId?: string,
  updatedTaxDto?: ApplyTaxExemptionDTO,
): Promise<CartResponse> => {
  let selectedExemptibleTax: ListItemData[] = [...currentlyNotExempted];
  if (_allAuths.length > 1 && updatedTaxDto === undefined) {
    selectedExemptibleTax = [...(await showMultipleAuthoritiesModal(_allAuths))];
  }
  const selectedAuthIds = selectedExemptibleTax.map(auth => auth.id);
  const taxIdsToExempt = separateNewAndReversedIds(_allAuths, selectedAuthIds, false);
  const taxIdsToReinstate = separateNewAndReversedIds(_allAuths, selectedAuthIds, true);
  let cartResponse: CartResponse = {};
  let taxDto: ApplyTaxExemptionDTO;

  if (updatedTaxDto !== undefined) {
    taxDto = updatedTaxDto;
  } else {
    taxDto = buildTaxExemptionDTO(taxIdsToExempt, taxIdsToReinstate);
  }

  try {
    cartResponse = lineId
      ? await exemptTaxByLine(transactionId, taxDto, lineId)
      : await exemptTax(transactionId, taxDto);

    if (lineId == undefined) {
      const remainingExemptions =
        currentlyExempted.length + taxIdsToExempt.length - taxIdsToReinstate.length;
      batch((): void => {
        store.dispatch(TransactionActions.setTransactionLevelTaxes(remainingExemptions > 0));
        store.dispatch(
          TransactionActions.setTaxAuthoritiesInCartList(remainingExemptions > 0 ? _allAuths : []),
        );
      });
    }
  } catch (error) {
    const taxData: TaxExemptionData = {
      transactionId: transactionId,
      taxExemptionDTO: buildTaxExemptionDTO(taxIdsToExempt, taxIdsToReinstate),
      lineId: lineId,
      taxExemptionErrorCode: '',
    };
    handleTaxException(error, taxData);
  }
  return cartResponse;
};

export const separateNewAndReversedIds = (
  exemptibleTax: ExemptibleTax[],
  selectedAuthIds: string[],
  exemptionsApplied: boolean,
): ExemptibleTax[] => {
  const taxExemptions = exemptibleTax.filter(auth => {
    if (!exemptionsApplied) {
      return selectedAuthIds.includes(auth.id) && !auth.wasExempted;
    } else {
      return !selectedAuthIds.includes(auth.id) && auth.wasExempted;
    }
  });

  return taxExemptions;
};

export const buildTaxExemptionDTO = (
  authoritiesToExempt: ExemptibleTax[],
  authoritiesToReinstate: ExemptibleTax[],
): ApplyTaxExemptionDTO => {
  const futureExemptedAuthIds = authoritiesToExempt.map(auth => auth.id);
  const futureReinstatedAuthIds = authoritiesToReinstate?.map(auth => auth.id);
  return {
    exemptedAuthorityIds: futureExemptedAuthIds,
    newExemptedAuthorityIds: futureExemptedAuthIds,
    reversedExemptedAuthorityIds: futureReinstatedAuthIds,
    taxExemptionPromptResults: {},
  };
};

export const getExemptedAuthorities = async (lineId?: string): Promise<string[]> => {
  const transactionId = store.getState().transaction.transactionId;
  const exemptibleTax = lineId
    ? await getExemptibleTaxByLine(transactionId, lineId)
    : await getExemptibleTax(transactionId);

  return exemptibleTax.reduce((ids: string[], tax: ExemptibleTax) => {
    if (tax.wasExempted) {
      ids.push(tax.id);
    }
    return ids;
  }, []);
};

export const separateAuthorities = (taxList: ExemptibleTax[]): separatedAuthorities => {
  const currentlyNotExempted = taxList.filter(taxAuth => !taxAuth.wasExempted);
  const currentlyExempted = taxList.filter(taxAuth => taxAuth.wasExempted);
  if (currentlyNotExempted.length + currentlyExempted.length === 0) {
    throw {
      message: 'No_Exempt_Items_Title',
      subMessage: 'No_Exempt_Items_Message',
    };
  }
  return { currentlyNotExempted, currentlyExempted };
};

export interface separatedAuthorities {
  currentlyNotExempted: ExemptibleTax[];
  currentlyExempted: ExemptibleTax[];
}
